import Radio from "../../../form/Radio";
import Input from "../../../form/Input";
import Select from "../../../form/select/Select";
import Select2 from "../../../form/select/Select2";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import useForm from "../../../../hooks/useForm";
import Form from "../../../form/Form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Checkbox from "../../../form/Checkbox";
import { useEffect, useState } from "react";
import {
  orderCancel,
  orderStatusChange,
} from "../../../../services/orderService";
import Button from "../../../form/Button";
import { toast } from "react-toastify";
import Errors from "../../../elements/Errors";
import Table from "../../../elements/table/Table";
import { lngDetect } from "../../../../helpers/lngDetect";
import Badge from "../../../elements/Badge";
import { classNames } from "../../../../helpers/classNames";
import paypal from "../../../../assets/images/paypalText.png";
import stripe from "../../../../assets/images/stripe.svg";
import Image from "../../../elements/Image";
import { t } from "i18next";

const initial_validation = {
  refundType: { required: true, message: "this field is required" },
  cancelReason: { required: true, message: "this field is required" },
  // receiveAmount: { required: true, message: "this field is required" },
  creditAmountType: { required: true, message: "this field is required" },
};

let validation = initial_validation;

const CancelorRefund = ({
  details,
  getResponse = () => {},
  close = () => {},
}) => {
  const queryClient = useQueryClient();
  const [percentageAmount, setPercentageAmount] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [receiveAmount, setReceiveAmount] = useState(0);
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
    resetField,
  } = useForm({
    validationRules: validation,
  });

  const orderSummary = {
    title: t("Order Summary"),
    links: [
      // { label: "Full Details", to: "#" },
      // { label: "Add Note", to: "#" },
    ],
    data: [
      {
        label: t("Buyer Name"),
        value: details?.billing_info?.buyer_name ?? "",
      },
      {
        label: t("Order Total"),
        value: `${
          details?.eventData?.currency?.symbol || "$"
        } ${details?.total?.toFixed(2)}`,
      },
      {
        label: t("Payment"),
        value:
          details?.payment?.status.charAt(0).toUpperCase() +
            details?.payment?.status.slice(1) || "Pending",
      },
      {
        label: t("Ticket Count"),
        value: (
          <ul className="grid grid-cols-3 gap-x-1 gap-y-2 pl-0">
            <li className="text-deep-purple text-sm ">
              {details?.orderItemData?.length || 0}
            </li>
          </ul>
        ),
      },
    ],
  };

  const cancelOptions = [
    {
      value: "full",
      heading: t("Cancel entire order"),
      subHeading: t("Cancel all orders, and choose refund options"),
    },
    {
      value: "partial",
      heading: t("Refund line item(s)"),
      subHeading: t("Cancel single order, and choose refund options"),
      ...(details?.status?.info === "processing" && { disabled: true }),
    },
  ];

  useEffect(() => {
    if (values?.creditAmountType === "full") {
      const already_refund_amount =
        (details?.refund || [])?.reduce(
          (acc, curent) => (acc || 0) + (curent?.refundAmount || 0),
          0
        ) || 0;
      const total_receive_amount =
        (values?.refundType === "full"
          ? details?.total - already_refund_amount
          : 0) -
        (values?.keepTransactionFee ? details?.organizationTransactionFee : 0);

      const total_remaining_amount = values?.keepTransactionFee
        ? details?.organizationTransactionFee
        : 0;

      setReceiveAmount(total_receive_amount);
      setRemaining(total_remaining_amount);
      return;
    }
    if (
      values?.creditAmountType === "percentage" ||
      values?.creditAmountType === "specific"
    ) {
      const total_remaining_amount = details?.total || 0;
      setRemaining(total_remaining_amount);
      return;
    }
  }, [values]);

  useEffect(() => {
    if (details?.status?.info === "processing") {
      delete validation.creditAmountType;
    } else {
      validation = initial_validation;
    }
  }, [details?.status?.info]);

  const {
    mutate: cancelOrder,
    isPending,
    error,
  } = useMutation({
    mutationFn: async (value) => {
      if (details?.status?.info !== "processing") {
        if (values?.refundType === "full") {
          const payload = {
            id: details?._id,
            cancelReason: value?.cancelReason,
            amount: Number(receiveAmount),
            refundType: value?.refundType,
            refund: {
              status: "requested",
              info: {
                refundType: value?.refundType,
                type: value?.creditAmountType,
                value: Number(percentageAmount),
                remainingAmount: Number(remaining),
              },
            },
          };
          const res = await orderCancel(payload);
          return { res };
        }
        if (values?.refundType === "partial") {
          const payload = {
            id: details?._id,
            refundType: values?.refundType,
            orderItemsData: Object.keys(values?.partialData || {})?.map(
              (key) => ({
                orderItemId: key,
                cancelReason: values?.partialData?.[key]?.cancelReason,
                amount: Number(values?.partialData?.[key]?.receiveAmount || 0),
                refundType: values?.refundType,
                refund: {
                  status: "requested",
                  info: {
                    refundType: values?.refundType,
                    type: values?.partialData?.[key]?.creditAmountType,
                    value: Number(values?.partialData?.[key]?.givenAmount || 0),
                    remainingAmount: Number(
                      values?.partialData?.[key]?.remainingAmount || 0
                    ),
                  },
                },
              })
            ),
          };
          const res = await orderCancel(payload);
          return { res };
        }
      } else {
        const res = await orderStatusChange({
          ids: details?._id,
          status: "canceled",
        });
        return { res, refundType: values?.refundType };
      }
    },
    onSuccess: ({ res, refundType }) => {
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["details", details?._id],
        });
        resetField();
        setFieldsValue({ refundType, cancelReason: "" });
        setPercentageAmount(0);
        setRemaining(0);
        setReceiveAmount(0);
        getResponse({ res, status: "refunded" });
      } else {
        toast.error(res?.message || res?.errors);
      }
    },
  });

  const tableHeadData = [
    {
      _id: "barcode",
      name: t("Barcode"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "ticket",
      name: t("Ticket"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "totalprice",
      name: t("Total Price"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "creditAmountType",
      name: t("Refund or credit amount"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "remaining",
      name: t("remaining amount"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
  ];

  const tableData = details?.orderItemData?.map((item) => {
    const item_value = values?.partialData?.[item?._id];

    return {
      _id: item?._id,
      rowData: [
        {
          _id: "barcode",
          type: "text",
          data: (
            <div>
              {item?.ticketInfo?.uid}
              <div className="text-xs text-slate-400">
                {item?.ticketInfo?.status}
              </div>
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "ticket",
          type: "text",
          data: (
            <div>
              <div className="max-h-40 overflow-y-auto">
                <p className="text-deep-purple text-sm ">
                  {lngDetect(item?.ticketInfo?.name)}
                </p>
              </div>
              <div className="text-xs text-slate-400">{t("eTicket")}</div>
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "price",
          type: "text",
          data: (
            <div>
              {details?.eventData?.currency?.symbol || "$"}
              {parseFloat(item?.finalPrice).toFixed(2)}
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "creditAmountType",
          type: "text",
          data: (
            <div>
              {item?.ticketInfo?.paymentType !== "free" ? (
                <>
                  {item?.remainingRefundableAmount > 0 && (
                    <>
                      <Select2
                        value={
                          values?.partialData?.[item?._id]?.creditAmountType
                        }
                        placeholder={"Select Option"}
                        dropdownData={[
                          {
                            name: t("Full amount"),
                            value: "full",
                          },
                          // {
                          //   name: "Amount after transaction fee deduction",
                          //   value: "after_fee",
                          // },
                          {
                            name: t("A percentage of amount"),
                            value: "percentage",
                          },
                          {
                            name: t("A specific amount"),
                            value: "specific",
                          },
                          // {
                          //   name: "No refund or credit",
                          //   value: "noRefund",
                          // },
                        ]}
                        dropdownButtonClass={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !rounded-md border-slate-300"
                        }
                        onChange={(val) => {
                          if (val) {
                            if (val === "full") {
                              setFieldsValue({
                                partialData: {
                                  ...values?.partialData,
                                  [item?._id]: {
                                    creditAmountType: val,
                                    receiveAmount:
                                      item?.remainingRefundableAmount ||
                                      item?.finalPrice ||
                                      0,
                                    remainingAmount: 0,
                                  },
                                },
                              });
                            } else {
                              setFieldsValue({
                                partialData: {
                                  ...values?.partialData,
                                  [item?._id]: {
                                    creditAmountType: val,
                                    receiveAmount: 0,
                                    remainingAmount: 0,
                                  },
                                },
                              });
                            }
                          } else {
                            const partialData = { ...values?.partialData };
                            delete partialData?.[item?._id];

                            setFieldsValue({ partialData });
                          }
                        }}
                      />

                      {values?.partialData?.[item?._id]?.creditAmountType ===
                        "percentage" && (
                        <div className="w-40 flex items-center gap-1">
                          <Input
                            value={
                              values?.partialData?.[item?._id]?.givenAmount || 0
                            }
                            inputPlaceholder={t("percentage")}
                            onChange={(e) => {
                              let val = Number(e.target.value || 0);

                              if (!isNaN(val)) {
                                setFieldsValue({
                                  partialData: {
                                    ...values?.partialData,
                                    [item?._id]: {
                                      ...values?.partialData?.[item?._id],
                                      percentageValue: val > 100 ? 100 : val,
                                      creditAmountType:
                                        values?.partialData?.[item?._id]
                                          ?.creditAmountType,
                                    },
                                  },
                                });
                                if (val > 100) val = 100;
                                if (val < 0) val = 0;

                                const total_remaining_amount =
                                  item?.remainingRefundableAmount || 0;
                                // Calculate percentage of total
                                const amount =
                                  (total_remaining_amount * val) / 100;

                                setFieldsValue({
                                  partialData: {
                                    ...values?.partialData,
                                    [item?._id]: {
                                      givenAmount: val > 100 ? 100 : val,
                                      creditAmountType:
                                        values?.partialData?.[item?._id]
                                          ?.creditAmountType,
                                      receiveAmount: amount,
                                      remainingAmount:
                                        total_remaining_amount - amount,
                                    },
                                  },
                                });
                              }
                            }}
                          />
                          <span>%</span>
                        </div>
                      )}

                      {values?.partialData?.[item?._id]?.creditAmountType ===
                        "specific" && (
                        <div className="w-40 flex items-center gap-1">
                          <span>
                            {details?.eventData?.currency?.symbol || "$"}
                          </span>
                          <Input
                            value={
                              values?.partialData?.[item?._id]?.givenAmount || 0
                            }
                            inputPlaceholder={t("amount")}
                            onChange={(e) => {
                              let val = Number(e.target.value || 0);
                              if (!isNaN(val)) {
                                const total_remaining_amount =
                                  item?.remainingRefundableAmount || 0;

                                setFieldsValue({
                                  partialData: {
                                    ...values?.partialData,
                                    [item?._id]: {
                                      ...values?.partialData?.[item?._id],
                                      specificValue:
                                        val > total_remaining_amount
                                          ? total_remaining_amount
                                          : val,
                                      creditAmountType:
                                        values?.partialData?.[item?._id]
                                          ?.creditAmountType,
                                    },
                                  },
                                });

                                if (val > total_remaining_amount)
                                  val = total_remaining_amount;
                                if (val < 0) val = 0;

                                setFieldsValue({
                                  partialData: {
                                    ...values?.partialData,
                                    [item?._id]: {
                                      givenAmount:
                                        val > total_remaining_amount
                                          ? total_remaining_amount
                                          : val,
                                      creditAmountType:
                                        values?.partialData?.[item?._id]
                                          ?.creditAmountType,
                                      receiveAmount: val,
                                      remainingAmount:
                                        total_remaining_amount - val,
                                    },
                                  },
                                });
                              }
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {item?.remainingRefundableAmount === 0 && (
                    <Badge type={"success"} label="refunded" />
                  )}
                </>
              ) : !item?.refund?.info?.updatedAt ? (
                <button
                  type="button"
                  onClick={() => {
                    if (values?.partialData?.[item?._id]) {
                      const partialData = {
                        ...values?.partialData,
                      };
                      delete partialData?.[item?._id];
                      setFieldsValue({ partialData: partialData });
                    } else {
                      setFieldsValue({
                        partialData: {
                          ...values?.partialData,
                          [item?._id]: {
                            creditAmountType: "full",
                            receiveAmount: 0,
                            remainingAmount: 0,
                          },
                        },
                      });
                    }
                  }}
                  className="text-red-500 font-medium text-sm hover:text-red-500/60 flex items-center gap-2"
                >
                  <Checkbox checked={!!values?.partialData?.[item?._id]} />
                  {t("Cancel free ticket")}
                </button>
              ) : (
                <div className="text-sm font-medium text-red-500">
                  {t("Cancelled")}
                </div>
              )}
            </div>
          ),
        },
        {
          _id: "remaining",
          type: "text",
          data: (
            <div>
              {details?.eventData?.currency?.symbol || "$"}
              {parseFloat(
                (item?.remainingRefundableAmount || 0) -
                  (item_value?.receiveAmount || 0)
              ).toFixed(2)}
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
      ],
    };
  });

  return (
    <>
      <div className="w-full px-2 md:px-6 grid grid-cols-12 gap-5">
        {details?.status?.info === "canceled" && (
          <div className="col-span-12 rounded-md font-medium bg-amber-100 text-amber-500 border-l-2 border-amber-500 p-3 px-4">
            {t("Order already canceled")}
          </div>
        )}
        <div className="col-span-12 lg:col-span-4">
          <div className="transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 border border-transparent hover:border-primary/10 hover:no-underline bg-white/80 backdrop-blur p-4 md:p-6 border-slate-200 group shadow rounded-3xl cursor-pointer sticky top-32 space-y-3">
            <div className="flex justify-between items-center gap-3">
              <div className="flex-shrink">
                <h2 className="text-xl md:text-2xl font-bold whitespace-nowrap">
                  {details?.orderId}
                </h2>
                <div className="text-sm text-slate-500 mt-1">
                  {dayjs(details?.createdAt).format(
                    "ddd, MMM DD, YYYY  |  hh:mm A"
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur rounded-3xl p-4 md:p-6 border border-slate-200 shadow">
              <h3 className="text-lg font-bold mb-4">{t("Order Summary")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                {orderSummary.data.map((item, idx) => (
                  <div className="relative" key={idx}>
                    <div className="text-slate-500">{item.label}</div>
                    <div className="font-medium text-slate-800">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-4 text-sm text-deep-purple">
                {orderSummary.links.map((link, idx) => (
                  <Link to={link.to} className="hover:underline" key={idx}>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
        {details?.status?.info !== "canceled" ? (
          <div className="space-y-6 col-span-12 lg:col-span-8 mb-6">
            <Form handleSubmit={handleSubmit} onSubmit={cancelOrder}>
              <div className="text-lg font-semibold">
                {t("Choose an option")}
              </div>
              <div className="flex flex-col md:flex-row gap-6">
                {cancelOptions?.map((opt, i) => (
                  <div
                    key={i}
                    className={classNames(
                      "flex gap-2 bg-slate-100/50 rounded-md p-3",
                      opt?.disabled ? "opacity-50 cursor-not-allowed" : ""
                    )}
                  >
                    <Radio
                      value={opt?.value}
                      checked={values?.refundType === opt?.value}
                      radioName={"refundType"}
                      onChange={(e) => {
                        const update_value = { ...(values || {}) };
                        if (e.target.value === "full") {
                          validation = initial_validation;
                          delete update_value?.partialData;
                        } else {
                          validation = {};
                        }
                        setFieldsValue({
                          ...update_value,
                          [e.target.name]: e.target.value,
                        });
                      }}
                      disabled={opt?.disabled}
                    />
                    <div className="space-y-1">
                      <div className="font-semibold text-base">
                        {opt?.heading}
                      </div>
                      <div className="text-xs font-medium text-surface-05">
                        {opt?.subHeading}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Errors {...formValidate(errors, "refundType")} />
              {values?.refundType !== "partial" && (
                <Input
                  label={t(
                    "Cancellation reason (this will be sent to the buyer)"
                  )}
                  inputName={"cancelReason"}
                  onChange={handleChange}
                  value={values?.cancelReason}
                  inputPlaceholder={"Enter reason"}
                  inputClasses={
                    "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                  }
                  // isDisabled={details?.status?.info === "processing"}
                  {...formValidate(errors, "cancelReason")}
                />
              )}
              <div
                className={classNames(
                  "space-y-3",
                  details?.status?.info === "processing" ? "opacity-50" : ""
                )}
              >
                {values?.refundType === "partial" && (
                  <Table
                    tableHeadData={tableHeadData}
                    tableData={tableData || []}
                    pageCount={1}
                    currentPage={1}
                    isLoder={false}
                    isOrderingDisabled={true}
                  />
                )}
                <div
                  className={
                    values?.refundType !== "partial"
                      ? "grid grid-cols-1 md:grid-cols-2 gap-6"
                      : ""
                  }
                >
                  {values?.refundType !== "partial" && (
                    <Select
                      label={"Refund or credit amount"}
                      placeholder={"Select Option"}
                      dropdownData={[
                        {
                          name: t("Full amount"),
                          value: "full",
                        },
                        {
                          name: t("A percentage of amount"),
                          value: "percentage",
                        },
                        {
                          name: t("A specific amount"),
                          value: "specific",
                        },
                        {
                          name: t("No refund or credit"),
                          value: "noRefund",
                        },
                      ]}
                      selectedValue={values?.creditAmountType}
                      onChange={(val) => {
                        setRemaining(0);
                        setReceiveAmount(0);
                        setPercentageAmount(0);
                        handleChange({
                          target: { name: "creditAmountType", value: val },
                        });
                      }}
                      isDisabled={details?.status?.info === "processing"}
                      dropdownButtonClass={
                        "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !rounded-md"
                      }
                      {...formValidate(errors, "creditAmountType")}
                    />
                  )}
                  {/* <Select
                    label={"Refund or credit method"}
                    placeholder={"Select credit method"}
                    dropdownData={[{ name: "stripe", value: "stripe" }]}
                    // selectedValue={values?.creditMethod}
                    selectedValue={"stripe"}
                    dropdownButtonClass={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !rounded-md"
                    }
                    onChange={(val) => {
                      handleChange({
                        target: { name: "creditMethod", value: val },
                      });
                    }}
                    isDisabled={details?.status?.info === "processing"}
                  /> */}
                  <div>
                    <div className="text-xs font-medium text-text-disabled mb-1">
                      {t("Refund or credit method")}
                    </div>
                    <Image
                      src={
                        details?.payment?.gateway === "stripe"
                          ? stripe
                          : details?.payment?.gateway === "paypal" && paypal
                      }
                      classes={"w-24 h-12 object-contain"}
                    />
                  </div>
                </div>
                {values?.refundType !== "partial" && (
                  <>
                    {values?.creditAmountType === "full" && (
                      <Checkbox
                        checkboxLabel={
                          <span className="font-medium">
                            {t("Keep transaction fee")} (
                            {details?.eventData?.currency?.symbol || "$"}
                            {details?.organizationTransactionFee || 0})
                          </span>
                        }
                        checked={values?.keepTransactionFee}
                        checkboxName={"keepTransactionFee"}
                        value={true}
                        onChange={handleChange}
                        isDisabled={details?.status?.info === "processing"}
                      />
                    )}
                    {values?.creditAmountType === "percentage" && (
                      <div className="w-40 flex items-center gap-1">
                        <Input
                          inputType="number"
                          value={percentageAmount}
                          inputPlaceholder="percentage"
                          onChange={(e) => {
                            let val = Number(e.target.value || 0);

                            if (!isNaN(val)) {
                              setPercentageAmount(val > 100 ? 100 : val);
                              if (val > 100) val = 100;
                              if (val < 0) val = 0;

                              const total_remaining_amount =
                                details?.total || 0;

                              // Calculate percentage of total
                              const amount =
                                (total_remaining_amount * val) / 100;
                              setReceiveAmount(amount);
                              setRemaining(total_remaining_amount - amount);
                            }
                          }}
                        />
                        <span>%</span>
                      </div>
                    )}

                    {values?.creditAmountType === "specific" && (
                      <div className="w-40 flex items-center gap-1">
                        <span>
                          {details?.eventData?.currency?.symbol || "$"}
                        </span>
                        <Input
                          inputType="number"
                          value={percentageAmount}
                          inputPlaceholder={t("amount")}
                          onChange={(e) => {
                            let val = Number(e.target.value || 0);
                            if (!isNaN(val)) {
                              const total_remaining_amount =
                                details?.total || 0;
                              setPercentageAmount(
                                val > total_remaining_amount
                                  ? total_remaining_amount
                                  : val
                              );

                              if (val > total_remaining_amount)
                                val = total_remaining_amount;
                              if (val < 0) val = 0;

                              setReceiveAmount(val);
                              setRemaining(total_remaining_amount - val);
                            }
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="space-y-6 p-5 bg-slate-100 rounded-xl">
                <div className="text-lg mb-6 font-semibold">{t("Summary")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1 w-full">
                    <div className="font-semibold text-base">
                      {t("Remaining order value")}
                    </div>
                    <div className="font-semibold text-2xl">
                      {details?.eventData?.currency?.symbol || "$"}
                      {parseFloat(
                        values?.refundType === "full"
                          ? remaining
                          : values?.refundType === "partial"
                          ? Object.values(values?.partialData || {})?.reduce(
                              (acc, cur) => acc + cur?.remainingAmount,
                              0
                            )
                          : 0
                      ).toFixed(2)}
                    </div>
                  </div>
                  <div className="space-y-1 w-full">
                    <div className="font-semibold text-base">
                      {t("Customer will receive")}
                    </div>
                    <div className="font-semibold text-2xl">
                      {details?.eventData?.currency?.symbol || "$"}
                      {parseFloat(
                        values?.refundType === "full"
                          ? receiveAmount
                          : values?.refundType === "partial"
                          ? Object.values(values?.partialData || {})?.reduce(
                              (acc, cur) => acc + cur?.receiveAmount,
                              0
                            )
                          : 0
                      ).toFixed(2)}
                    </div>
                  </div>
                </div>
                {details?.status?.info !== "canceled" && (
                  <Button
                    buttonLabel={t("Cancel Order")}
                    buttonType="submit"
                    buttonClasses={
                      "h-10 font-medium bg-red-500 text-white rounded-md flex items-center justify-center p-5 ml-auto"
                    }
                    {...(isPending
                      ? {
                          loading: true,
                          buttonIconPosition: "left",
                          isDisable: true,
                        }
                      : (values?.refundType === "full"
                          ? isNaN(Number(remaining)) ||
                            isNaN(Number(receiveAmount))
                          : values?.refundType === "partial" &&
                            (isNaN(
                              Number(
                                Object.values(
                                  values?.partialData || {}
                                )?.reduce(
                                  (acc, cur) => acc + cur?.receiveAmount,
                                  0
                                )
                              )
                            ) ||
                              isNaN(
                                Number(
                                  Object.values(
                                    values?.partialData || {}
                                  )?.reduce(
                                    (acc, cur) => acc + cur?.remainingAmount,
                                    0
                                  )
                                )
                              ))) && {
                          loading: true,
                          buttonIconPosition: "left",
                          isDisable: true,
                        })}
                  />
                )}
              </div>
            </Form>
          </div>
        ) : (
          <div className="col-span-7 space-y-3">
            {details?.refund?.map((refund, i) => (
              <ul
                className="space-y-2 border border-slate-300/60 shadow bg-slate-100/50 p-5 rounded-2xl [&>*]:capitalize"
                key={i?.toString()}
              >
                {/* <li className="grid grid-cols-3 gap-4">
                  <span className="font-medium rounded col-span-1">
                    Order cancel type
                  </span>
                  <span className="text-sm font-medium col-span-2 text-gray-600">
                    <span className="mr-3 font-bold">:</span>{" "}
                    {refund?.info?.refundType
                      ? `${refund?.info?.refundType} amount`
                      : "No Type"}
                  </span>
                </li> */}
                <li className="grid grid-cols-3 gap-4">
                  <span className="font-medium rounded col-span-1">
                    {t("cancel reason")}
                  </span>
                  <span className="text-sm font-medium col-span-2 text-gray-600">
                    <span className="mr-3 font-bold">:</span>{" "}
                    {details?.cancelReason || "No reason"}
                  </span>
                </li>
                {/* <li className="grid grid-cols-3 gap-4">
                  <span className="font-medium rounded col-span-1">
                    payment refund Type
                  </span>
                  <span className="text-sm font-medium col-span-2 text-gray-600">
                    <span className="mr-3 font-bold">:</span>{" "}
                    {refund?.info?.type || "No refund"}
                  </span>
                </li> */}
                {/* <li className="grid grid-cols-3 gap-4">
                  <span className="font-medium rounded col-span-1">
                    Refund amount by
                  </span>
                  <span className="text-sm font-medium col-span-2 text-gray-600">
                    <span className="mr-3 font-bold">:</span> Stripe
                  </span>
                </li> */}
                {/* <li className="grid grid-cols-3 gap-4">
                  <span className="font-medium rounded col-span-1">
                    remaining amount
                  </span>
                  <span className="text-sm font-medium col-span-2 text-gray-600">
                    <span className="mr-3 font-bold">:</span>{" "}
                    {refund?.info?.remainingAmount || "0"}
                  </span>
                </li> */}
                <li className="grid grid-cols-3 gap-4">
                  <span className="font-medium rounded col-span-1">
                    {t("refund amount")}
                  </span>
                  <span className="text-sm font-medium col-span-2 text-gray-600">
                    <span className="mr-3 font-bold">:</span>{" "}
                    {details?.eventData?.currency?.symbol || "$"}{" "}
                    {parseFloat(refund?.refundAmount).toFixed(2) || "0.00"}
                  </span>
                </li>
                <li className="grid grid-cols-3 gap-4">
                  <span className="font-medium rounded col-span-1">
                    {t("refunded at")}
                  </span>
                  <span className="text-sm font-medium col-span-2 text-gray-600">
                    <span className="mr-3 font-bold">:</span>
                    {dayjs(refund?.refundedAt).format("DD MMM YYYY")}
                  </span>
                </li>
              </ul>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CancelorRefund;
